import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { clsx } from 'clsx';
import Backdrop from 'components/Modal/Backdrop';

import { getOwnershipContagionLevel } from '../utils/monitoringResultsUtils';

import type {
  IOwnershipFinding,
  IOwnershipInvestigationNode,
  IOwnershipMonitoredEntity,
} from 'constants/interfaces/IMonitoringSignalResult';
import type IOwnershipAnalysisModal from './interfaces/IOwnershipAnalysisModal';

import styles from './sass/OwnershipAnalysisModal.module.scss';

type TOwnershipDetailView = 'summary' | 'diagram';

interface ITreeRow {
  kind: 'node' | 'finding',
  depth: number,
  key: string,
  node?: IOwnershipInvestigationNode,
  finding?: IOwnershipFinding,
}

const getSupplierFindings = (
  entity: IOwnershipMonitoredEntity,
  findings: IOwnershipFinding[],
): IOwnershipFinding[] => findings.filter((finding) => finding.supplier_unit_id === entity.unitId);

const countTreeFindingIds = (nodes: IOwnershipInvestigationNode[] = []): number => (
  nodes.reduce((count, node) => (
    count + (node.finding_ids?.length || 0) + countTreeFindingIds(node.children || [])
  ), 0)
);

const flattenInvestigationTree = (
  nodes: IOwnershipInvestigationNode[],
  depth: number,
  expandedNodeIds: Set<string>,
  findingsById: Map<string, IOwnershipFinding>,
): ITreeRow[] => nodes.flatMap((node) => {
  const rows: ITreeRow[] = [{
    kind: 'node', depth, key: `node-${node.id}`, node,
  }];
  const isExpanded = expandedNodeIds.has(node.id);

  if (!isExpanded) {
    return rows;
  }

  const childRows = node.children?.length
    ? flattenInvestigationTree(node.children, depth + 1, expandedNodeIds, findingsById)
    : [];

  const findingRows: ITreeRow[] = (node.finding_ids || [])
    .map((findingId) => findingsById.get(findingId))
    .filter((finding): finding is IOwnershipFinding => Boolean(finding))
    .map((finding) => ({
      kind: 'finding' as const, depth: depth + 1, key: `finding-${finding.id}`, finding,
    }));

  return [...rows, ...childRows, ...findingRows];
});

const pickPrimaryOwnershipFinding = (
  supplierFindings: IOwnershipFinding[],
): IOwnershipFinding | null => (
  supplierFindings.find((finding) => finding.severity === 'critical')
  || supplierFindings.find((finding) => finding.severity === 'high')
  || supplierFindings[0]
  || null
);

const isSupplierImpactEligible = (
  supplierFindings: IOwnershipFinding[],
): boolean => supplierFindings.some(
  (finding) => finding.severity === 'high' || finding.severity === 'critical',
);

const buildSupplierTreeRows = (
  entity: IOwnershipMonitoredEntity,
  supplierFindings: IOwnershipFinding[],
  expandedNodeIds: Set<string>,
  findingsById: Map<string, IOwnershipFinding>,
): ITreeRow[] => {
  if (entity.investigation_tree?.length) {
    return flattenInvestigationTree(entity.investigation_tree, 1, expandedNodeIds, findingsById);
  }

  return supplierFindings.map((finding) => ({
    kind: 'finding' as const,
    depth: 1,
    key: `flat-finding-${finding.id}`,
    finding,
  }));
};

function OwnershipAnalysisModal({
  open,
  results,
  onClose,
  showImpactScenarioCta = false,
  isImpactScenarioCreating = false,
  onCreateImpactScenario,
}: IOwnershipAnalysisModal): React.JSX.Element | null {
  const { t } = useTranslation();
  const entities = useMemo(() => results?.entities || [], [results]);
  const findings = useMemo(() => results?.findings || [], [results]);
  const findingsById = useMemo(
    () => new Map(findings.map((finding) => [finding.id, finding])),
    [findings],
  );

  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [expandedSupplierIds, setExpandedSupplierIds] = useState<Set<number>>(new Set());
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [detailView, setDetailView] = useState<TOwnershipDetailView>('summary');

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    document.body.style.overflow = 'hidden';

    const firstEntity = entities[0];

    if (firstEntity) {
      setSelectedSupplierId((currentId) => currentId ?? firstEntity.unitId);
      setExpandedSupplierIds((current) => (current.size ? current : new Set([firstEntity.unitId])));

      const topNodeIds = (firstEntity.investigation_tree || []).map((node) => node.id);

      setExpandedNodeIds((current) => (current.size ? current : new Set(topNodeIds)));

      const firstSupplierFindings = getSupplierFindings(firstEntity, findings);
      const primaryFinding = pickPrimaryOwnershipFinding(firstSupplierFindings);

      setSelectedFindingId((currentId) => currentId ?? primaryFinding?.id ?? findings[0]?.id ?? null);
    } else {
      const firstHighSeverityFinding = findings.find(
        (finding) => finding.severity === 'high' || finding.severity === 'critical',
      );

      setSelectedFindingId((currentId) => currentId ?? firstHighSeverityFinding?.id ?? findings[0]?.id ?? null);
    }

    return () => {
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) {
    return null;
  }

  const selectSupplier = (unitId: number) => {
    setSelectedSupplierId(unitId);

    const entity = entities.find((candidate) => candidate.unitId === unitId);
    const supplierFindings = entity ? getSupplierFindings(entity, findings) : [];
    const primaryFinding = pickPrimaryOwnershipFinding(supplierFindings);

    setSelectedFindingId(primaryFinding?.id ?? null);
    setDetailView('summary');
  };

  const toggleSupplier = (unitId: number) => {
    selectSupplier(unitId);
    setExpandedSupplierIds((current) => {
      const next = new Set(current);

      if (next.has(unitId)) {
        next.delete(unitId);

        return next;
      }

      next.add(unitId);

      const entity = entities.find((candidate) => candidate.unitId === unitId);
      const topNodeIds = (entity?.investigation_tree || []).map((node) => node.id);

      setExpandedNodeIds((currentNodeIds) => (
        new Set(Array.from(currentNodeIds).concat(topNodeIds))
      ));

      return next;
    });
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNodeIds((current) => {
      const next = new Set(current);

      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }

      return next;
    });
  };

  const selectFinding = (findingId: string, view: TOwnershipDetailView = 'summary') => {
    setSelectedFindingId(findingId);
    setDetailView(view);
  };

  const selectedFinding = findingsById.get(selectedFindingId || '') || null;
  const selectedSupplier = entities.find((entity) => entity.unitId === selectedSupplierId) || null;
  const selectedSupplierFindings = selectedSupplier
    ? getSupplierFindings(selectedSupplier, findings)
    : [];
  const canCreateImpactForSelectedSupplier = Boolean(
    showImpactScenarioCta
    && onCreateImpactScenario
    && selectedSupplier
    && isSupplierImpactEligible(selectedSupplierFindings),
  );
  const highRiskFindingCount = findings.filter(
    (finding) => finding.severity === 'high' || finding.severity === 'critical',
  ).length;

  return createPortal(
    <Backdrop>
      <div className={styles.root}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {t('valueChainMap.monitoring.results.ownershipAnalysisTitle')}
          </h2>
          <button
            type="button"
            className={styles.iconButton}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <CloseIcon aria-hidden="true" />
          </button>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.kpis}>
            <span className={styles.kpiChip}>
              {t('valueChainMap.monitoring.results.ownershipModalKpiSuppliers', {
                count: entities.length,
              })}
            </span>
            <span className={styles.kpiChip}>
              {t('valueChainMap.monitoring.results.ownershipModalKpiFindings', {
                count: findings.length,
              })}
            </span>
            <span className={styles.kpiChip}>
              {t('valueChainMap.monitoring.results.ownershipModalKpiHighRisk', {
                count: highRiskFindingCount,
              })}
            </span>
          </div>
          <p className={styles.hint}>
            {t('valueChainMap.monitoring.results.ownershipModalHint')}
          </p>
        </div>

        <div className={styles.layout}>
          <div className={styles.tablePane}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('common.supplier')}</th>
                  <th>{t('valueChainMap.monitoring.results.ownershipModalGroup')}</th>
                  <th>{t('valueChainMap.monitoring.results.ownershipModalContagion')}</th>
                  <th>{t('valueChainMap.monitoring.results.ownershipModalTrend')}</th>
                  <th>{t('valueChainMap.monitoring.results.ownershipModalExposure')}</th>
                </tr>
              </thead>
              <tbody>
                {entities.length ? entities.map((entity) => {
                  const isExpanded = expandedSupplierIds.has(entity.unitId);
                  const isSelected = selectedSupplierId === entity.unitId;
                  const supplierFindings = getSupplierFindings(entity, findings);
                  const treeIssueCount = countTreeFindingIds(entity.investigation_tree || []);
                  const issueCount = Math.max(supplierFindings.length, treeIssueCount);
                  const scoreLevel = getOwnershipContagionLevel(entity.contagion_score);
                  const treeRows = isExpanded
                    ? buildSupplierTreeRows(entity, supplierFindings, expandedNodeIds, findingsById)
                    : [];

                  return (
                    <React.Fragment key={entity.unitId}>
                      <tr
                        className={clsx(styles.supplierRow, {
                          [styles.supplierRowSelected]: isSelected,
                          [styles.supplierRowClickable]: true,
                        })}
                        onClick={() => selectSupplier(entity.unitId)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            selectSupplier(entity.unitId);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-pressed={isSelected}
                      >
                        <td>
                          <div className={styles.supplierCell}>
                            <button
                              type="button"
                              className={styles.expandButton}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleSupplier(entity.unitId);
                              }}
                              aria-expanded={isExpanded}
                              aria-label={entity.name}
                            >
                              {isExpanded ? '▾' : '▸'}
                            </button>
                            <button
                              type="button"
                              className={styles.supplierLinkButton}
                              onClick={(event) => {
                                event.stopPropagation();
                                selectSupplier(entity.unitId);
                              }}
                            >
                              {entity.name}
                            </button>
                          </div>
                        </td>
                        <td>{entity.ownership_profile.corporate_group || '—'}</td>
                        <td>{entity.contagion_score}</td>
                        <td>
                          {t(`valueChainMap.monitoring.results.ownershipTrend.${entity.trend || 'stable'}`)}
                        </td>
                        <td>
                          <span className={clsx(styles.exposureBadge, styles[`exposure_${scoreLevel}`])}>
                            {t('valueChainMap.monitoring.results.ownershipModalIssueCount', {
                              count: issueCount,
                            })}
                          </span>
                        </td>
                      </tr>

                      {isExpanded && !treeRows.length ? (
                        <tr className={styles.emptyRow}>
                          <td colSpan={5}>
                            {t('valueChainMap.monitoring.results.ownershipModalNoNodes')}
                          </td>
                        </tr>
                      ) : null}

                      {treeRows.map((row) => {
                        if (row.kind === 'node' && row.node) {
                          const { node } = row;
                          const nodeIssueCount = (node.finding_ids?.length || 0)
                            + countTreeFindingIds(node.children || []);
                          const canExpand = Boolean(node.children?.length || node.finding_ids?.length);
                          const isNodeExpanded = expandedNodeIds.has(node.id);

                          return (
                            <tr key={row.key} className={styles.nodeRow}>
                              <td colSpan={5}>
                                <div
                                  className={clsx(styles.nodeRowInner, { [styles.nodeRowFlagged]: node.risk_flag })}
                                  style={{ paddingLeft: Math.min(row.depth, 4) * 16 }}
                                >
                                  {canExpand ? (
                                    <button
                                      type="button"
                                      className={styles.expandButton}
                                      onClick={() => toggleNode(node.id)}
                                      aria-expanded={isNodeExpanded}
                                    >
                                      {isNodeExpanded ? '▾' : '▸'}
                                    </button>
                                  ) : (
                                    <span className={styles.expandSpacer} aria-hidden="true" />
                                  )}
                                  <div className={styles.nodeCopy}>
                                    <div className={styles.nodeTitle}>{node.name}</div>
                                    <div className={styles.nodeMeta}>
                                      {node.role}
                                      {node.jurisdiction ? ` · ${node.jurisdiction}` : ''}
                                      {typeof node.ownership_stake_pct === 'number'
                                        ? ` · ${node.ownership_stake_pct}% stake`
                                        : ''}
                                    </div>
                                  </div>
                                  <span
                                    className={clsx(styles.exposureBadge, {
                                      [styles.exposure_high]: node.risk_flag,
                                      [styles.exposure_low]: !node.risk_flag,
                                    })}
                                  >
                                    {t('valueChainMap.monitoring.results.ownershipModalLinkedCount', {
                                      count: nodeIssueCount,
                                    })}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        if (row.kind === 'finding' && row.finding) {
                          const { finding } = row;

                          return (
                            <tr
                              key={row.key}
                              className={clsx(styles.issueRow, {
                                [styles.issueRowActive]: selectedFindingId === finding.id,
                              })}
                            >
                              <td colSpan={5}>
                                <div
                                  className={styles.issueRowInner}
                                  style={{ paddingLeft: Math.min(row.depth, 4) * 16 + 12 }}
                                >
                                  <div className={styles.issueCopy}>
                                    <div className={styles.nodeTitle}>{finding.title}</div>
                                    <div className={styles.nodeMeta}>
                                      {t(
                                        `valueChainMap.monitoring.results.ownershipTheme.${finding.theme}`,
                                        { defaultValue: finding.theme },
                                      )}
                                      {' · '}
                                      {finding.observed_at}
                                      {typeof finding.confidence === 'number'
                                        ? ` · ${Math.round(finding.confidence * 100)}%`
                                        : ''}
                                    </div>
                                  </div>
                                  <div className={styles.issueActions}>
                                    <button
                                      type="button"
                                      className={clsx(styles.viewLinkButton, {
                                        [styles.viewLinkButtonActive]: selectedFindingId === finding.id
                                          && detailView === 'summary',
                                      })}
                                      onClick={() => selectFinding(finding.id, 'summary')}
                                    >
                                      {t('valueChainMap.monitoring.results.ownershipModalSummaryTab')}
                                    </button>
                                    <button
                                      type="button"
                                      className={clsx(styles.viewLinkButton, {
                                        [styles.viewLinkButtonActive]: selectedFindingId === finding.id
                                          && detailView === 'diagram',
                                      })}
                                      onClick={() => selectFinding(finding.id, 'diagram')}
                                    >
                                      {t('valueChainMap.monitoring.results.ownershipModalDiagramTab')}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return null;
                      })}
                    </React.Fragment>
                  );
                }) : (
                  <tr className={styles.emptyRow}>
                    <td colSpan={5}>
                      {t('valueChainMap.monitoring.results.noOwnershipFindings')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.detailPane}>
            {selectedFinding ? (
              <>
                <div className={styles.detailHeader}>
                  <h3 className={styles.detailTitle}>{selectedFinding.title}</h3>
                  <div className={styles.detailToggle}>
                    <button
                      type="button"
                      className={clsx(styles.detailViewButton, {
                        [styles.detailViewButtonActive]: detailView === 'summary',
                      })}
                      onClick={() => setDetailView('summary')}
                    >
                      {t('valueChainMap.monitoring.results.ownershipModalSummaryTab')}
                    </button>
                    <button
                      type="button"
                      className={clsx(styles.detailViewButton, {
                        [styles.detailViewButtonActive]: detailView === 'diagram',
                      })}
                      onClick={() => setDetailView('diagram')}
                    >
                      {t('valueChainMap.monitoring.results.ownershipModalDiagramTab')}
                    </button>
                  </div>
                </div>

                <div className={styles.detailMeta}>
                  {selectedFinding.supplier_name}
                  {' · '}
                  {t(
                    `valueChainMap.monitoring.results.ownershipTheme.${selectedFinding.theme}`,
                    { defaultValue: selectedFinding.theme },
                  )}
                  {' · '}
                  {t(`valueChainMap.monitoring.results.riskSeverity.${selectedFinding.severity}`)}
                </div>

                {detailView === 'summary' ? (
                  <div className={styles.summaryCard}>
                    <div className={styles.summaryField}>
                      <span className={styles.summaryLabel}>
                        {t('valueChainMap.monitoring.results.ownershipModalObserved')}
                      </span>
                      <span className={styles.summaryValue}>{selectedFinding.observed_at}</span>
                    </div>
                    <div className={styles.summaryField}>
                      <span className={styles.summaryLabel}>
                        {t('valueChainMap.monitoring.results.ownershipModalConfidence')}
                      </span>
                      <span className={styles.summaryValue}>
                        {typeof selectedFinding.confidence === 'number'
                          ? `${Math.round(selectedFinding.confidence * 100)}%`
                          : '—'}
                      </span>
                    </div>
                    <div className={styles.summaryField}>
                      <span className={styles.summaryLabel}>
                        {t('valueChainMap.monitoring.results.ownershipContagionMechanism')}
                      </span>
                      <span className={styles.summaryValue}>
                        {selectedFinding.contagion_mechanism || '—'}
                      </span>
                    </div>
                    <div className={styles.summaryField}>
                      <span className={styles.summaryLabel}>
                        {t('valueChainMap.monitoring.results.ownershipShortageRisk')}
                      </span>
                      <span className={styles.summaryValue}>
                        {selectedFinding.shortage_risk?.summary || '—'}
                      </span>
                    </div>
                    <div className={styles.summaryField}>
                      <span className={styles.summaryLabel}>
                        {t('valueChainMap.monitoring.results.ownershipRecommendedAction')}
                      </span>
                      <span className={styles.summaryValue}>
                        {selectedFinding.recommended_action || '—'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.diagramTrail}>
                      {(selectedFinding.link_path || []).map((node, nodeIndex) => (
                        <div
                          key={`${selectedFinding.id}-${node.entity_name}`}
                          className={clsx(styles.trailStep, { [styles.trailStepOrigin]: nodeIndex === 0 })}
                        >
                          <div className={styles.trailCard}>
                            <div className={styles.trailRole}>{node.role}</div>
                            <div className={styles.trailName}>{node.entity_name}</div>
                          </div>
                          {nodeIndex < (selectedFinding.link_path?.length || 0) - 1 ? (
                            <span className={styles.trailArrow} aria-hidden="true">→</span>
                          ) : (
                            <span className={styles.trailImpact}>
                              {t('valueChainMap.monitoring.results.ownershipModalImpact')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className={styles.trailInterpretation}>
                      <strong>
                        {t('valueChainMap.monitoring.results.ownershipModalTrailInterpretation')}
                        :
                        {' '}
                      </strong>
                      {selectedFinding.contagion_mechanism || '—'}
                    </p>
                  </>
                )}

                {selectedFinding.related_materials?.length ? (
                  <div className={styles.materialsList}>
                    {selectedFinding.related_materials.map((material) => (
                      <span key={material} className={styles.materialChip}>{material}</span>
                    ))}
                  </div>
                ) : null}

                {canCreateImpactForSelectedSupplier ? (
                  <div className={styles.impactBridgeSection}>
                    <p className={styles.impactBridgeDescription}>
                      {t('valueChainMap.monitoring.ownership.impactBridgeCtaFromSupplierDescription')}
                    </p>
                    <button
                      type="button"
                      className={styles.impactBridgeButton}
                      disabled={isImpactScenarioCreating}
                      onClick={() => {
                        if (!selectedSupplier || !onCreateImpactScenario) {
                          return;
                        }

                        onClose();
                        onCreateImpactScenario([selectedSupplier.unitId]);
                      }}
                    >
                      {t('valueChainMap.monitoring.ownership.impactBridgeCtaFromSupplier')}
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <div className={styles.emptyDetailWrap}>
                <p className={styles.emptyDetail}>
                  {selectedSupplier
                    ? t('valueChainMap.monitoring.results.ownershipModalNoNodes')
                    : t('valueChainMap.monitoring.results.ownershipModalSelectIssue')}
                </p>
                {canCreateImpactForSelectedSupplier ? (
                  <div className={styles.impactBridgeSection}>
                    <p className={styles.impactBridgeDescription}>
                      {t('valueChainMap.monitoring.ownership.impactBridgeCtaFromSupplierDescription')}
                    </p>
                    <button
                      type="button"
                      className={styles.impactBridgeButton}
                      disabled={isImpactScenarioCreating}
                      onClick={() => {
                        if (!selectedSupplier || !onCreateImpactScenario) {
                          return;
                        }

                        onClose();
                        onCreateImpactScenario([selectedSupplier.unitId]);
                      }}
                    >
                      {t('valueChainMap.monitoring.ownership.impactBridgeCtaFromSupplier')}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </Backdrop>,
    document.body,
  );
}

export default OwnershipAnalysisModal;
